import { useSyncExternalStore } from 'react';

/**
 * Reactively track the `.dark` class on <html> without depending on ThemeProvider.
 * For non-token surfaces that must pick a concrete light/dark value in JS — the
 * syntax-highlighter theme — and that also render in bare tests.
 *
 * One shared MutationObserver backs every consumer (subscribed via useSyncExternalStore),
 * so N code blocks cost one observer, not N.
 */
const listeners = new Set<() => void>();
let observer: MutationObserver | null = null;

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  if (!observer && typeof document !== 'undefined') {
    observer = new MutationObserver(() => {
      for (const l of listeners) l();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }
  return () => {
    listeners.delete(onStoreChange);
    if (listeners.size === 0 && observer) {
      observer.disconnect();
      observer = null;
    }
  };
}

function getSnapshot(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

export function useIsDarkMode(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
